import React, { useState, useRef } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import {
  AttachFile,
  Close,
  DeleteOutline,
  InsertDriveFile,
  PictureAsPdf,
  Image as ImageIcon,
  Description,
} from "@material-ui/icons";
import { green } from "@material-ui/core/colors";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { BotaoCancelar, BotaoConfirmar } from "../BotoesModal";

// Deve refletir o backend (config/uploadInternalNotes.js).
const ALLOWED_EXTENSIONS = [
  ".pdf", ".jpg", ".jpeg", ".png",
  ".doc", ".docx", ".xls", ".xlsx", ".txt",
];
const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
  textarea: {
    width: "100%",
  },
  dropzone: {
    marginTop: theme.spacing(2),
    border: "2px dashed #c0c0c0",
    borderRadius: 8,
    padding: theme.spacing(2),
    textAlign: "center",
    color: "#707070",
    cursor: "pointer",
    transition: "border-color .2s, background-color .2s",
  },
  dropzoneActive: {
    borderColor: green[500],
    backgroundColor: "rgba(76, 175, 80, 0.08)",
  },
  hiddenInput: {
    display: "none",
  },
  fileList: {
    marginTop: theme.spacing(1),
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  fileItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 8px",
    borderRadius: 6,
    backgroundColor: "#f4f4f4",
    marginTop: 6,
  },
  fileInfo: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
  },
  fileName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: 13,
  },
  fileSize: {
    fontSize: 11,
    color: "#909090",
  },
}));

const formatBytes = (bytes) => {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

const fileIcon = (file) => {
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".pdf")) return <PictureAsPdf fontSize="small" />;
  if (/\.(jpg|jpeg|png)$/.test(name)) return <ImageIcon fontSize="small" />;
  if (/\.(doc|docx|txt)$/.test(name)) return <Description fontSize="small" />;
  return <InsertDriveFile fontSize="small" />;
};

const InternalNoteModal = ({ modalOpen, onClose, ticketId, userId }) => {
  const classes = useStyles();
  const [comentario, setComentario] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const resetState = () => {
    setComentario("");
    setFiles([]);
    setLoading(false);
    setDragOver(false);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose();
  };

  const validateAndAddFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    const accepted = [];
    for (const file of incoming) {
      const lower = (file.name || "").toLowerCase();
      const extOk = ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
      if (!extOk) {
        toast.error(`Tipo de arquivo não permitido: ${file.name}`);
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        toast.error(`Arquivo maior que ${MAX_FILE_MB}MB: ${file.name}`);
        continue;
      }
      accepted.push(file);
    }
    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
  };

  const handleFileInputChange = (e) => {
    validateAndAddFiles(e.target.files);
    // permite re-selecionar o mesmo arquivo
    e.target.value = "";
  };

  const handleDrop = (e) => {
    // stopPropagation: impede que o evento suba pela árvore React até o
    // <div onDrop> da conversa (no MessageInput), evitando que o arquivo vire
    // anexo da conversa em vez de anexo do comentário interno.
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    validateAndAddFiles(e.dataTransfer.files);
  };

  // Engole qualquer evento de drag/drop ocorrido DENTRO do modal, para que não
  // chegue ao handler de anexo da conversa. O drop útil é tratado no dropzone.
  const swallowDragEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!comentario.trim()) {
      toast.error("Digite uma anotação interna.");
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      // A ordem importa: o ticketId precisa chegar antes dos arquivos para o
      // multer montar o diretório de destino (internal-notes/<ticketId>/...).
      formData.append("ticketId", ticketId);
      formData.append("userId", userId);
      formData.append("comentario", comentario);
      formData.append("tipo", "comentario_interno");
      files.forEach((file) => formData.append("anexos", file));

      await api.post("/mensageminfoicode", formData);

      toast.success("Anotação interna salva.");
      resetState();
      onClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      onDragEnter={swallowDragEvent}
      onDragOver={swallowDragEvent}
      onDragLeave={swallowDragEvent}
      onDrop={swallowDragEvent}
    >
      <DialogTitle id="internal-note-dialog-title">Comentário Interno</DialogTitle>
      <DialogContent dividers>
        <TextField
          className={classes.textarea}
          label="Anotação interna"
          placeholder="Digite uma anotação interna…"
          multiline
          minRows={4}
          variant="outlined"
          value={comentario}
          onChange={(e) => setComentario(e.target.value)}
          disabled={loading}
          autoFocus
          required
        />

        <div
          className={`${classes.dropzone} ${dragOver ? classes.dropzoneActive : ""}`}
          onClick={() => inputRef.current && inputRef.current.click()}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            e.stopPropagation();
            setDragOver(false);
          }}
          onDrop={handleDrop}
        >
          <AttachFile fontSize="small" />
          <Typography variant="body2">
            Clique ou arraste arquivos aqui para anexar
          </Typography>
          <Typography variant="caption">
            PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, TXT — até {MAX_FILE_MB}MB cada
          </Typography>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(",")}
            className={classes.hiddenInput}
            onChange={handleFileInputChange}
          />
        </div>

        {files.length > 0 && (
          <ul className={classes.fileList}>
            {files.map((file, index) => (
              <li className={classes.fileItem} key={`${file.name}-${index}`}>
                {fileIcon(file)}
                <span className={classes.fileInfo}>
                  <span className={classes.fileName}>{file.name}</span>
                  <span className={classes.fileSize}>{formatBytes(file.size)}</span>
                </span>
                <IconButton
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                  disabled={loading}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
      <DialogActions>
        <BotaoCancelar onClick={handleClose} disabled={loading} startIcon={<Close />}>
          Cancelar
        </BotaoCancelar>
        <BotaoConfirmar
          onClick={handleSave}
          loading={loading}
          disabled={!comentario.trim()}
        >
          Salvar
        </BotaoConfirmar>
      </DialogActions>
    </Dialog>
  );
};

export default InternalNoteModal;
